import { state } from "./app-state";
import {
  DOC_GROUPS,
  DOCS,
  getDocById,
  resolveDocId,
  STORAGE,
  VOLUME_LABEL,
} from "./constants";
import { ui } from "./dom";

export function readNavExpanded(): Record<string, boolean> {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE.navGroups) || "{}");
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
}

export function writeNavExpanded(map: Record<string, boolean>): void {
  localStorage.setItem(STORAGE.navGroups, JSON.stringify(map));
}

function docButtonHtml(doc: (typeof DOCS)[number]): string {
  return `<button type="button" data-doc="${doc.id}">
      <span class="doc-index">${doc.index}</span>
      <span>${doc.shortTitle}</span>
      <span class="doc-open-count" data-doc-open-count="${doc.id}"></span>
    </button>`;
}

export function buildNavigation(): void {
  const expanded = readNavExpanded();
  const current = getDocById(state.currentDocId);

  const parts: string[] = [];
  for (const group of DOC_GROUPS) {
    const groupDocs = DOCS.filter((doc) => doc.group === group.id);
    if (!groupDocs.length) continue;

    const groupKey = `g:${group.id}`;
    const groupOpen =
      expanded[groupKey] ??
      (current?.group === group.id || group.id === "draft");

    if (group.id === "draft") {
      const volumes = [...new Set(groupDocs.map((doc) => doc.volume || 1))].sort();
      let volumeHtml = "";
      for (const volume of volumes) {
        const volDocs = groupDocs.filter((doc) => (doc.volume || 1) === volume);
        const volKey = `v:${volume}`;
        const volOpen =
          expanded[volKey] ?? (current?.group === "draft" && current.volume === volume);
        volumeHtml += `<div class="doc-volume ${volOpen ? "is-open" : ""}" data-nav-key="${volKey}">
          <button type="button" class="volume-toggle" data-nav-toggle="${volKey}" aria-expanded="${volOpen}">
            <span>${VOLUME_LABEL[volume] || `卷${volume}`}</span>
            <span class="nav-chevron" aria-hidden="true"></span>
          </button>
          <div class="volume-items">${volDocs.map(docButtonHtml).join("")}</div>
        </div>`;
      }
      parts.push(`<div class="doc-group ${groupOpen ? "is-open" : ""}" data-nav-key="${groupKey}">
        <button type="button" class="group-toggle" data-nav-toggle="${groupKey}" aria-expanded="${groupOpen}">
          <span>${group.label}</span>
          <span class="nav-chevron" aria-hidden="true"></span>
        </button>
        <div class="group-items group-items--volumes">${volumeHtml}</div>
      </div>`);
      continue;
    }

    parts.push(`<div class="doc-group ${groupOpen ? "is-open" : ""}" data-nav-key="${groupKey}">
      <button type="button" class="group-toggle" data-nav-toggle="${groupKey}" aria-expanded="${groupOpen}">
        <span>${group.label}</span>
        <span class="nav-chevron" aria-hidden="true"></span>
      </button>
      <div class="group-items">${groupDocs.map(docButtonHtml).join("")}</div>
    </div>`);
  }

  ui.docNav.innerHTML = parts.join("");

  ui.boardDocFilter.innerHTML = [
    '<option value="all">全部文档</option>',
    '<option value="home">项目总意见</option>',
    ...DOC_GROUPS.flatMap((group) => {
      const groupDocs = DOCS.filter((doc) => doc.group === group.id);
      if (!groupDocs.length) return [];
      return [
        `<optgroup label="${group.label}">`,
        ...groupDocs.map((doc) => `<option value="${doc.id}">${doc.title}</option>`),
        "</optgroup>",
      ];
    }),
  ].join("");
  ui.boardDocFilter.value = state.boardDocId;
}

export function setActiveDoc(id: string): void {
  const resolved = resolveDocId(id);
  ui.docNav.querySelectorAll<HTMLButtonElement>("button[data-doc]").forEach((button) => {
    button.classList.toggle("active", button.dataset.doc === resolved);
  });
  // ensure group/volume containing active doc is open
  const doc = getDocById(resolved);
  if (!doc) return;
  const expanded = readNavExpanded();
  expanded[`g:${doc.group}`] = true;
  if (doc.volume) expanded[`v:${doc.volume}`] = true;
  writeNavExpanded(expanded);
  ui.docNav.querySelector(`[data-nav-key="g:${doc.group}"]`)?.classList.add("is-open");
  if (doc.volume) {
    ui.docNav.querySelector(`[data-nav-key="v:${doc.volume}"]`)?.classList.add("is-open");
  }
}
